import { useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { ArrowLeft, Save, Loader2 } from 'lucide-react';
import { api } from '@/lib/api';
import type { VenueArea, BookingType } from '@/types';

const areaSchema = z.object({
  bookingTypeId: z.string().min(1, 'Booking type is required'),
  name: z.string().min(1, 'Area name is required'),
  description: z.string().optional(),
  isActive: z.boolean().default(true),
});

type AreaFormData = z.infer<typeof areaSchema>;

export default function AreaForm() {
  const { venueId, areaId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const preselectedType = searchParams.get('type');
  const isEditing = Boolean(areaId);

  const { data: venue } = useQuery({
    queryKey: ['venue', venueId],
    queryFn: async () => {
      const response = await api.get(`/admin/venues/${venueId}`);
      return response.data;
    },
  });

  const { data: bookingTypes } = useQuery<BookingType[]>({
    queryKey: ['booking-types'],
    queryFn: async () => {
      const response = await api.get('/admin/booking-types');
      return response.data.data;
    },
  });

  const { data: area, isLoading: areaLoading } = useQuery<VenueArea>({
    queryKey: ['venue-area', venueId, areaId],
    queryFn: async () => {
      const response = await api.get(`/admin/venues/${venueId}/areas/${areaId}`);
      return response.data;
    },
    enabled: isEditing,
  });

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<AreaFormData>({
    resolver: zodResolver(areaSchema),
    defaultValues: {
      bookingTypeId: preselectedType || '',
      name: '',
      description: '',
      isActive: true,
    },
  });

  useEffect(() => {
    if (area) {
      reset({
        bookingTypeId: area.booking_type_id,
        name: area.name,
        description: area.description || '',
        isActive: area.is_active,
      });
    }
  }, [area, reset]);

  const createMutation = useMutation({
    mutationFn: async (data: AreaFormData) => {
      const response = await api.post(`/admin/venues/${venueId}/areas`, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['venue-areas', venueId] });
      navigate(`/venues/${venueId}/booking-config/areas${preselectedType ? `?type=${preselectedType}` : ''}`);
    },
  });

  const updateMutation = useMutation({
    mutationFn: async (data: AreaFormData) => {
      await api.put(`/admin/venues/${venueId}/areas/${areaId}`, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['venue-areas', venueId] });
      queryClient.invalidateQueries({ queryKey: ['venue-area', venueId, areaId] });
      navigate(`/venues/${venueId}/booking-config/areas${preselectedType ? `?type=${preselectedType}` : ''}`);
    },
  });

  const onSubmit = async (data: AreaFormData) => {
    if (isEditing) {
      await updateMutation.mutateAsync(data);
    } else {
      await createMutation.mutateAsync(data);
    }
  };

  if (isEditing && areaLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center gap-4 mb-6">
        <button
          onClick={() => navigate(`/venues/${venueId}/booking-config/areas${preselectedType ? `?type=${preselectedType}` : ''}`)}
          className="p-2 hover:bg-gray-100 rounded-md"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {isEditing ? 'Edit Area' : 'Create Area'}
          </h1>
          <p className="text-sm text-gray-500">{(venue as Record<string, unknown>)?.name as string}</p>
        </div>
      </div>

      <div className="card max-w-2xl">
        <form onSubmit={handleSubmit(onSubmit)} className="p-6 space-y-6">
          {(createMutation.error || updateMutation.error) && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md text-sm">
              {(createMutation.error as Error)?.message || (updateMutation.error as Error)?.message || 'An error occurred'}
            </div>
          )}

          <div>
            <label htmlFor="bookingTypeId" className="label">
              Booking Type
            </label>
            <select
              id="bookingTypeId"
              className="input"
              {...register('bookingTypeId')}
              disabled={isEditing}
            >
              <option value="">Select a booking type...</option>
              {bookingTypes?.map((type) => (
                <option key={type.id} value={type.id}>
                  {type.name}
                </option>
              ))}
            </select>
            {errors.bookingTypeId && (
              <p className="mt-1 text-sm text-red-600">{errors.bookingTypeId.message}</p>
            )}
          </div>

          <div>
            <label htmlFor="name" className="label">
              Area Name
            </label>
            <input
              id="name"
              type="text"
              className="input"
              placeholder="e.g., Main Dining Room, Outdoor Terrace"
              {...register('name')}
            />
            {errors.name && (
              <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>
            )}
          </div>

          <div>
            <label htmlFor="description" className="label">
              Description (Optional)
            </label>
            <textarea
              id="description"
              className="input"
              rows={3}
              placeholder="Describe this area..."
              {...register('description')}
            />
          </div>

          {isEditing && (
            <div className="flex items-center gap-3">
              <input
                id="isActive"
                type="checkbox"
                className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                {...register('isActive')}
              />
              <label htmlFor="isActive" className="text-sm text-gray-700">
                Area is active and available for bookings
              </label>
            </div>
          )}

          <div className="flex justify-end gap-3 pt-4 border-t">
            <button
              type="button"
              onClick={() => navigate(`/venues/${venueId}/booking-config/areas${preselectedType ? `?type=${preselectedType}` : ''}`)}
              className="btn btn-secondary"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="btn btn-primary"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  {isEditing ? 'Update Area' : 'Create Area'}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
